import { useEffect, useRef } from 'react';

/**
 * Batch H, point 3 — bare Ctrl / bare Tab cycles through the main sections
 * (Deposit, Withdraw, Support, Inquiry) when the handler/admin is sitting
 * at one of those pages (not inside an open chat). Ctrl alone = previous
 * section, Tab alone = next section.
 *
 * "Ctrl alone" means the Control key was pressed and released without any
 * other key going down in between — tracked via keydown/keyup rather than
 * a single event, since there's no native "modifier tapped alone" event.
 * Tab is only intercepted when nothing editable is focused, so normal
 * Tab-based form/field navigation elsewhere on the page (e.g. the chat-list
 * search box) is never disturbed.
 *
 * @param {string[]} sections - ordered section keys, e.g. ['deposit','withdrawal','support','inquiry']
 * @param {string} current - the currently active section key
 * @param {(next: string) => void} onChange - called with the new section key
 * @param {boolean} enabled - false disables the whole hook (e.g. a chat is open, or role is ineligible)
 */
const useSectionCycleKeys = (sections, current, onChange, enabled) => {
  const ctrlDownRef = useRef(false);
  const ctrlUsedWithOtherKeyRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const isEditableFocused = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = (el.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
    };

    const isModalOpen = () => (
      document.querySelector('[role="dialog"]') ||
      document.querySelector('[data-modal-open="true"]') ||
      document.querySelector('.modal-open')
    );

    const cycle = (dir) => {
      const idx = sections.indexOf(current);
      if (idx === -1) return;
      const nextIdx = (idx + dir + sections.length) % sections.length;
      onChange(sections[nextIdx]);
    };

    const onKeyDown = (e) => {
      if (e.key === 'Control') {
        ctrlDownRef.current = true;
        ctrlUsedWithOtherKeyRef.current = false;
        return;
      }
      if (e.ctrlKey) {
        // Ctrl is being held down together with another key (e.g. Ctrl+D) —
        // that combo is handled elsewhere (GlobalKeyboardShortcuts.jsx);
        // this just marks that the eventual Control-keyup isn't a bare tap.
        ctrlUsedWithOtherKeyRef.current = true;
        return;
      }
      // Item-2 fix: the custom Tab-cycle shortcut has been REMOVED. Tab now
      // behaves as plain native browser Tab everywhere (moves focus to the
      // next field only) — it never triggers a section change. The bare-
      // Control-key section cycle below is intentionally kept unchanged.
    };

    const onKeyUp = (e) => {
      if (e.key !== 'Control') return;
      const wasBareTap = ctrlDownRef.current && !ctrlUsedWithOtherKeyRef.current;
      ctrlDownRef.current = false;
      ctrlUsedWithOtherKeyRef.current = false;
      if (wasBareTap && !isEditableFocused() && !isModalOpen()) {
        cycle(-1);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [enabled, sections, current, onChange]);
};

export default useSectionCycleKeys;
